import { Component, Prop, Watch } from 'vue-property-decorator';
import EditorBase from '../EditorBase';
import { TextInput, Button, StatusSwitch, ColorPicker } from 'streamlabs-beaker';
import ComponentHelper, { EComponentType, IComponent, IComponentCss } from '@/helpers/editor';

@Component({
  components: {
    STextInput: TextInput,
    SButton: Button,
    SStatusSwitch: StatusSwitch,
    SColorPicker: ColorPicker,
  },
})
export default class Position extends EditorBase {
  x: number = 0;
  y: number = 0;
  w: number = 0;
  h: number = 0;
  angle: number = 0;
  opacity: number = 100;
  borderEnabled: boolean = false;
  borderWidth: number = 1;
  borderRadius: number = 0;
  borderColor: string = '#000000';
  lockAspectRatio: boolean = false;
  shouldTriggerSave: boolean = false;
  isRelativeValueUpdating: boolean = false;

  @Prop({ default: false })
  lockX: boolean;

  @Prop({ default: false })
  lockY: boolean;

  @Prop({ default: false })
  lockW: boolean;

  @Prop({ default: false })
  lockH: boolean;

  @Prop({ default: true })
  allowHorizontalAlign: boolean;

  @Prop({ default: true })
  allowVerticalAlign: boolean;

  @Prop({ default: false })
  allowVerticalAlignTop: boolean;

  @Prop({ default: false })
  allowVerticalAlignBottom: boolean;

  @Prop({ default: true })
  allowAspectRatio: boolean;

  @Prop({ default: true })
  allowAngle: boolean;

  @Prop({ default: true })
  allowOpacity: boolean;

  @Prop({ default: true })
  allowBorder: boolean;

  get positionObject(): Partial<IComponent> {
    const positionObject = {
      x: Math.round(this.x),
      y: Math.round(this.y),
      w: Math.round(this.w),
      h: Math.round(this.h),
    };

    if (this.isVerticalEditor) {
      return {
        verticalSettings: {
          ...positionObject,
        },
      };
    }

    return positionObject;
  }

  get cssObject(): Partial<IComponentCss> {
    return {
      opacity: this.opacity / 100,
      rotate: this.angle,
      borderEnabled: this.borderEnabled,
      borderWidth: this.borderWidth,
      borderRadius: this.borderRadius,
      borderColor: this.borderColor,
    };
  }

  get maxX(): number {
    return (this.resolution / 9) * 16 - this.selectedComponent.x;
  }

  get maxY(): number {
    return this.resolution - this.selectedComponent.h;
  }

  get resolution(): number {
    if (!this.selectedProject || !this.selectedProject.settings.resolution) return 720;

    if (this.isVerticalEditor) {
      return parseInt(this.selectedProject.settings.resolution) / 9 * 16;
    }

    return parseInt(this.selectedProject.settings.resolution);
  }

  async mounted() {
    await this.readSelectedComponentProperties();

    this.shouldTriggerSave = true;
  }

  async beforeDestroy() {
    await this.resetValues();
  };

  async resetValues() {
    this.x = 0;
    this.y = 0;
    this.w = 0;
    this.h = 0;
    this.angle = 0;
    this.opacity = 100;
    this.borderEnabled = false;
    this.borderWidth = 1;
    this.borderRadius = 0;
    this.borderColor = '#000000';
    this.lockAspectRatio = false;
    this.shouldTriggerSave = false;
  }

  @Watch('selectedEditor')
  @Watch('selectedComponent', { deep: true })
  async loadComponentProperties() {
    if (!this.selectedComponent) return;

    await this.readSelectedComponentProperties();
  }

  async readSelectedComponentProperties() {
    this.x = this.selectedComponentCanvasSettings.x;
    this.y = this.selectedComponentCanvasSettings.y;
    this.w = this.selectedComponentCanvasSettings.w;
    this.h = this.selectedComponentCanvasSettings.h;
    this.opacity = Math.round((this.selectedComponentCanvasSettings.css?.opacity ?? 1) * 100);
    this.angle = this.selectedComponentCanvasSettings.css?.rotate ?? 0;
    this.lockAspectRatio = this.selectedComponentCanvasSettings.lockAspectRatio ?? false;

    if (this.selectedComponentCanvasSettings.css?.borderEnabled) {
      this.borderEnabled = this.selectedComponentCanvasSettings.css?.borderEnabled;
      this.borderWidth = this.selectedComponentCanvasSettings.css?.borderWidth;
      this.borderRadius = this.selectedComponentCanvasSettings.css?.borderRadius as number;
      this.borderColor = this.selectedComponentCanvasSettings.css?.borderColor;
    }
  }

  @Watch('x')
  handleXChange(newValue, oldValue) {
    if (
      oldValue == null ||
      newValue === oldValue ||
      this.x === this.selectedComponentCanvasSettings.x
    )
      return;

    this.onComponentUpdate({ ...this.selectedComponent, ...this.positionObject }, this.shouldTriggerSave);
  }

  @Watch('y')
  handleYChange(newValue, oldValue) {
    if (
      oldValue == null ||
      newValue === oldValue ||
      this.y === this.selectedComponentCanvasSettings.y
    )
      return;

    this.onComponentUpdate({ ...this.selectedComponent, ...this.positionObject }, this.shouldTriggerSave);
  }

  @Watch('h')
  async handleHChange(newValue, oldValue) {
    if (
      this.isRelativeValueUpdating ||
      oldValue == null ||
      newValue === oldValue ||
      this.h === this.selectedComponentCanvasSettings.h
    )
      return;
    if (this.h <= 0) return;

    if (
      (this.selectedComponentCanvasSettings.lockAspectRatio && this.selectedComponentCanvasSettings.aspectRatio) ||
      this.selectedComponent.type === EComponentType.gameCapture
    ) {
      this.isRelativeValueUpdating = true;
      this.w = Math.round(
        this.h * (
          this.selectedComponentCanvasSettings.aspectRatio ??
          ComponentHelper.defaultGameCaptureSettings.aspectRatio
        )
      );
    }

    await this.onComponentUpdate({ ...this.selectedComponent, ...this.positionObject }, this.shouldTriggerSave);
    this.isRelativeValueUpdating = false;
  }

  @Watch('w')
  async handleWChange(newValue, oldValue) {
    if (
      this.isRelativeValueUpdating ||
      oldValue == null ||
      newValue === oldValue ||
      this.w === this.selectedComponentCanvasSettings.w
    )
      return;
    if (this.w <= 0) return;

    if (
      (this.selectedComponentCanvasSettings.lockAspectRatio && this.selectedComponentCanvasSettings.aspectRatio) ||
      this.selectedComponent.type === EComponentType.gameCapture
    ) {
      this.isRelativeValueUpdating = true;
      this.h = Math.round(
        this.w / (
          this.selectedComponentCanvasSettings.aspectRatio ??
          ComponentHelper.defaultGameCaptureSettings.aspectRatio
        )
      );
    }

    await this.onComponentUpdate({ ...this.selectedComponent, ...this.positionObject }, this.shouldTriggerSave);
    this.isRelativeValueUpdating = false;
  }

  /**
   *
   * for CSS watchers, updates when
   * check if old values are null or undefined because newly added component doesn't have a default value
   * check if when switching components, the values equal to each other
   * check if when switching components on reset, the new value does not equal to default value
   * check if selectedComponent's value exists and equals to state value
   *
   */
  @Watch('angle')
  handleAngleChange(newValue, oldValue) {
    if (
      oldValue == null ||
      newValue === oldValue ||
      (this.angle === this.selectedComponentCanvasSettings.css?.rotate)
    )
      return;

    this.updateCssChanges();
  }

  @Watch('opacity')
  handleOpacityChange(newValue, oldValue) {
    if (
      oldValue == null ||
      newValue === oldValue ||
      (this.opacity / 100 === this.selectedComponentCanvasSettings.css?.opacity)
    )
      return;

    this.updateCssChanges();
  }

  @Watch('lockAspectRatio')
  handleLockAspectRatioChange(newValue, oldValue) {
    if (
      oldValue == null ||
      newValue === oldValue ||
      this.lockAspectRatio === this.selectedComponentCanvasSettings?.lockAspectRatio
    )
      return;

    let data: Partial<IComponent> = {
      id: this.selectedComponent.id,
    };

    const h = this.lockAspectRatio && this.selectedComponentCanvasSettings.aspectRatio ?
      Math.round(this.w / this.selectedComponentCanvasSettings.aspectRatio) :
      this.h;

    if (this.isVerticalEditor) {
      data.verticalSettings = {
        ...this.selectedComponentCanvasSettings,
        lockAspectRatio: this.lockAspectRatio,
        aspectRatio: this.w / this.h,
        h,
      };
    } else {
      data = {
        ...this.selectedComponentCanvasSettings,
        ...data,
        lockAspectRatio: this.lockAspectRatio,
        aspectRatio: this.w / this.h,
        h,
      };
    }

    this.onComponentUpdate(data, this.shouldTriggerSave);
  }

  @Watch('borderEnabled')
  handleBorderEnabledChange(newValue, oldValue) {
    if (
      oldValue == null ||
      newValue === oldValue ||
      (this.borderEnabled === this.selectedComponentCanvasSettings.css?.borderEnabled)
    )
      return;

    this.updateCssChanges();
  }

  @Watch('borderWidth')
  handleBorderWidthChange(newValue, oldValue) {
    if (
      oldValue == null ||
      newValue === oldValue ||
      (this.borderWidth === this.selectedComponent.css?.borderWidth)
    )
      return;

    this.updateCssChanges();
  }

  @Watch('borderRadius')
  handleBorderRadiusChange(newValue, oldValue) {
    if (
      oldValue == null ||
      newValue === oldValue ||
      (this.borderRadius === this.selectedComponent.css?.borderRadius)
    )
      return;

    this.updateCssChanges();
  }

  @Watch('borderColor')
  handleBorderColorChange(newValue, oldValue) {
    if (
      oldValue == null ||
      newValue === oldValue ||
      (this.borderColor === this.selectedComponent.css?.borderColor)
    )
      return;

    this.updateCssChanges();
  }

  updateCssChanges() {
    let data: Partial<IComponent> = {};

    if (this.isVerticalEditor) {
      data.verticalSettings = {
        css: {
          ...this.selectedComponent.verticalSettings?.css || {},
          ...this.cssObject,
        },
      };
    } else {
      data.css = {
        ...this.selectedComponent.css || {},
        ...this.cssObject,
      };
    }

    this.onComponentUpdate({ ...this.selectedComponent, ...data }, this.shouldTriggerSave);
  }

  onHorizontalAlign() {
    // 16:9 aspect ratio on horizontal editor
    this.x = this.isVerticalEditor ?
      ((this.resolution / 16) * 9) / 2 - this.w / 2 :
      ((this.resolution / 9) * 16) / 2 - this.w / 2;
  }

  onVerticalAlign() {
    this.y = Math.round(this.resolution / 2 - this.h / 2);
  }

  onVerticalAlignBottom() {
    this.y = Math.round(this.resolution - this.h);
  }

  onVerticalAlignTop() {
    this.y = 0;
  }

  async onInputBlur(_event, dataName: string, min: number, max: number) {
    if (!this[dataName]) return;

    if (this[dataName] > max) {
      await this.$nextTick(() => {
        this[dataName] = max;
      });
    }
    if (this[dataName] < min) {
      await this.$nextTick(() => {
        this[dataName] = min;
      });
    }
  }
}
